import * as React from 'react';
import styled from 'styled-components';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Tooltip } from '../atoms';
import { getConfirmationStatus, EboxConfirmationStatus } from '../api/publicEbox';

type EboxLookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'found'; data: EboxConfirmationStatus }
  | { kind: 'notFound'; confirmationNumber: string }
  | { kind: 'error'; message: string };

// Confirmation number format, e.g. EBOX-0126-PV6J8W:
// "EBOX-" + 4 digits + "-" + 6 letters/digits. Case-insensitive (the lookup
// itself is case-insensitive server-side).
const EBOX_FORMAT = /^EBOX-\d{4}-[A-Z0-9]{6}$/i;

// Reduce an address to comparable alphanumeric tokens (lowercased, punctuation
// dropped). "2831 Ringling Blvd Unit A103 , Sarasota, FL 34237" →
// ['2831','ringling','blvd','unit','a103','sarasota','fl','34237'].
const addressTokens = (address: string): string[] =>
  (address || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

// Loose address match: the smaller token set must be ≥70% contained in the
// larger. Tolerates punctuation, case, and one side carrying extra tokens
// (e.g. a unit number) while still flagging a genuinely different address.
const addressesMatch = (a: string, b: string): boolean => {
  const aTokens = addressTokens(a);
  const bTokens = addressTokens(b);
  if (!aTokens.length || !bTokens.length) return false;
  const [smaller, larger] = aTokens.length <= bTokens.length ? [aTokens, bTokens] : [bTokens, aTokens];
  const largerSet = new Set(larger);
  const contained = smaller.filter(token => largerSet.has(token)).length;
  return contained / smaller.length >= 0.7;
};

const EboxTooltipContent = () => (
  <TooltipBody>
    <TooltipHeading>What is an EBox?</TooltipHeading>
    <p>
      An <strong>EBox</strong> is a publicly shareable permit box identified by a <strong>Confirmation Number</strong>. Enter that number
      and click
      <strong> Check</strong> to look it up in the EBox system — no login needed.
    </p>
    <TooltipHeading>What Check returns</TooltipHeading>
    <ul>
      <li>
        <strong>Status</strong> — “Available” (the number isn’t assigned yet) or “Not Available” (already in use).
      </li>
      <li>
        <strong>Live</strong> — whether the EBox for this number is currently active.
      </li>
      <li>
        When a number is in use and active, it also returns the <strong>Public URL</strong>, <strong>Project name</strong>, and{' '}
        <strong>Address</strong> on that EBox.
      </li>
    </ul>
  </TooltipBody>
);

interface Props {
  /** Controlled value (the project's saved confirmation number). */
  value?: string;
  /** Fires on every keystroke so the parent form can persist it. */
  onChange?: (value: string) => void;
  /** The project's own address, compared against an in-use EBox's address. */
  projectAddress?: string;
}

/**
 * Self-contained EBox Confirmation-Status lookup: a Confirmation Number input
 * + Check button that calls the public EBox API and renders the result.
 * Lives in the "Edit Project" form. When `value`/`onChange` are passed it acts
 * as a controlled field so the entered number is saved with the project.
 */
const EboxLookup = ({ value, onChange, projectAddress }: Props) => {
  const [internal, setInternal] = React.useState(value ?? '');
  const input = value !== undefined ? value : internal;
  const setInput = (next: string) => {
    if (onChange) onChange(next);
    else setInternal(next);
  };
  const [state, setState] = React.useState<EboxLookupState>({
    kind: 'idle',
  });

  const lookup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const confirmationNumber = input.trim();
    if (!confirmationNumber) return;

    if (!EBOX_FORMAT.test(confirmationNumber)) {
      setState({
        kind: 'error',
        message: 'Invalid format. Use EBOX-0126-PV6J8W — “EBOX-”, 4 digits, then 6 letters/digits.',
      });
      return;
    }

    setState({ kind: 'loading' });

    try {
      const { data } = await getConfirmationStatus(confirmationNumber);
      setState({ kind: 'found', data });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setState({ kind: 'notFound', confirmationNumber });
      } else if (status === 400) {
        setState({
          kind: 'error',
          message: 'A confirmation number is required.',
        });
      } else {
        setState({
          kind: 'error',
          message: 'Could not reach the confirmation-status service. Please try again.',
        });
      }
    }
  };

  return (
    <EboxWrapper>
      <SectionTitleRow>
        <SectionTitle>Ebox</SectionTitle>
        <Tooltip title={<EboxTooltipContent />} interactive>
          <InfoIconButton type="button" aria-label="About the Ebox lookup">
            <InfoOutlinedIcon fontSize="inherit" />
          </InfoIconButton>
        </Tooltip>
      </SectionTitleRow>
      <FieldBlock>
        <FieldLabel>Ebox Number</FieldLabel>
        <EboxRow onSubmit={lookup}>
          <EboxInput
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="e.g. EBOX-0326-NUC3X4"
            aria-label="Confirmation Number"
          />
          <EboxButton type="submit" disabled={state.kind === 'loading' || !input.trim()}>
            {state.kind === 'loading' ? 'Checking…' : 'Check'}
          </EboxButton>
        </EboxRow>
      </FieldBlock>
      <EboxResult state={state} projectAddress={projectAddress} />
    </EboxWrapper>
  );
};

const EboxResult = ({ state, projectAddress }: { state: EboxLookupState; projectAddress?: string }) => {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return null;
  }

  if (state.kind === 'error') {
    return <Callout $tone="danger">{state.message}</Callout>;
  }

  if (state.kind === 'notFound') {
    return (
      <Callout $tone="neutral">
        No record found for <strong>{state.confirmationNumber}</strong>. This confirmation number is unknown.
      </Callout>
    );
  }

  const { data } = state;
  const isAvailable = data.Status === 'Available';
  const eboxFullAddress =
    data.Status === 'Not Available' && data.Address
      ? data.Address.FullAddress ||
        [data.Address.Street, data.Address.Street2, data.Address.City, data.Address.State, data.Address.ZipCode].filter(Boolean).join(', ')
      : '';
  const addressLines =
    data.Status === 'Not Available' && data.Live && data.Address
      ? [data.Address.BusinessNameOrFullName, eboxFullAddress].filter(Boolean)
      : [];

  // When the number is already in use, compare its address with this project's.
  const canCompareAddress = !isAvailable && !!eboxFullAddress && !!(projectAddress || '').trim();
  const addressMatch = canCompareAddress ? addressesMatch(eboxFullAddress, projectAddress || '') : null;

  const statusLabel = isAvailable ? data.Status : 'Ebox already taken';

  return (
    <ResultCard>
      <StatusRow>
        <StatusChip $available={isAvailable}>{statusLabel}</StatusChip>
        <LiveBadge $live={data.Live}>{data.Live ? 'Live' : 'Not live'}</LiveBadge>
      </StatusRow>
      {data.PublicUrl && data.PublicUrl.startsWith('https://') ? (
        <ResultRow>
          <ResultLabel>Public URL</ResultLabel>
          <ResultValue>
            <Anchor href={data.PublicUrl} target="_blank" rel="noreferrer">
              {data.PublicUrl}
            </Anchor>
          </ResultValue>
        </ResultRow>
      ) : null}
      {data.ProjectName ? (
        <ResultRow>
          <ResultLabel>Project name</ResultLabel>
          <ResultValue>{data.ProjectName}</ResultValue>
        </ResultRow>
      ) : null}
      {addressLines.length ? (
        <ResultRow>
          <ResultLabel>Address</ResultLabel>
          <ResultValue>
            {addressLines.map((line, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i}>{line}</div>
            ))}
          </ResultValue>
        </ResultRow>
      ) : null}
      {addressMatch === true ? (
        <AddressNotice $tone="match">
          <NoticeTitle>✓ Address Match</NoticeTitle>
          <NoticeBody>This EBox’s address matches this project’s address.</NoticeBody>
        </AddressNotice>
      ) : null}
      {addressMatch === false ? (
        <AddressNotice $tone="mismatch">
          <NoticeTitle>⚠ Address does not match</NoticeTitle>
          <NoticeBody>
            This EBox’s address doesn’t match this project’s address. Make sure you’re linking the right EBox before you save.
          </NoticeBody>
        </AddressNotice>
      ) : null}
    </ResultCard>
  );
};

const EboxWrapper = styled.div`
  margin-top: var(--space-6);
  padding-top: var(--space-5);
  border-top: 1px solid var(--neutral-200);
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
`;

const SectionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: var(--fw-semibold);
  color: var(--neutral-800);
`;

const InfoIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--neutral-500);
  cursor: pointer;
  font-size: 18px;
  line-height: 0;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const TooltipBody = styled.div`
  font-size: 0.75rem;
  line-height: 1.5;
  padding: 4px 2px;

  p {
    margin: 0 0 8px;
  }

  ul {
    margin: 0;
    padding-left: 16px;
  }

  li {
    margin-bottom: 4px;
  }
`;

const TooltipHeading = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-2) 0;
`;

const FieldLabel = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  color: var(--neutral-800);
  line-height: 21px;
`;

const EboxRow = styled.form`
  display: flex;
  gap: var(--space-3);
  align-items: center;
  flex-wrap: wrap;
`;

const EboxInput = styled.input`
  flex: 1;
  min-width: 240px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--neutral-300);
  border-radius: var(--radius-md);
  background: var(--neutral-0);
  font-size: var(--fs-base);
  color: var(--neutral-800);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }
  &:hover {
    border-color: var(--neutral-400);
  }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const EboxButton = styled.button`
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: var(--fw-semibold);
  font-size: var(--fs-base);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }
  &:disabled {
    background: var(--neutral-300);
    cursor: not-allowed;
  }
`;

const Callout = styled.div<{ $tone: 'danger' | 'neutral' }>`
  margin-top: var(--space-3);
  padding: 12px 14px;
  border-radius: var(--radius-md);
  font-size: var(--fs-base);
  background: ${({ $tone }) => ($tone === 'danger' ? 'var(--danger-50)' : 'var(--neutral-50)')};
  border: 1px solid ${({ $tone }) => ($tone === 'danger' ? 'var(--danger-500)' : 'var(--neutral-200)')};
  color: ${({ $tone }) => ($tone === 'danger' ? 'var(--danger-500)' : 'var(--neutral-700)')};
`;

const ResultCard = styled.div`
  margin-top: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-md);
  background: var(--neutral-50);
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
`;

const StatusChip = styled.span<{ $available: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: var(--radius-full);
  font-size: 0.8125rem;
  font-weight: var(--fw-semibold);
  background: ${({ $available }) => ($available ? 'var(--success-50)' : 'var(--danger-50)')};
  color: ${({ $available }) => ($available ? 'var(--success-700)' : 'var(--danger-500)')};
  border: 1px solid ${({ $available }) => ($available ? 'var(--success-500)' : 'var(--danger-500)')};
`;

const LiveBadge = styled.span<{ $live: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: var(--radius-full);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  background: ${({ $live }) => ($live ? 'var(--primary-50)' : 'var(--danger-50)')};
  color: ${({ $live }) => ($live ? 'var(--primary-700)' : 'var(--danger-500)')};
  border: 1px solid ${({ $live }) => ($live ? 'var(--primary-100)' : 'var(--danger-500)')};
`;

const ResultRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  border-top: 1px solid var(--neutral-200);

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

const ResultLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  align-self: center;
`;

const ResultValue = styled.div`
  font-size: var(--fs-base);
  color: var(--neutral-800);
  word-break: break-word;
`;

const Anchor = styled.a`
  color: var(--primary-700);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const AddressNotice = styled.div<{ $tone: 'match' | 'mismatch' }>`
  margin-top: var(--space-3);
  padding: 10px 12px;
  border-radius: var(--radius-md);
  background: ${({ $tone }) => ($tone === 'match' ? 'var(--success-50)' : 'var(--danger-50)')};
  border: 1px solid ${({ $tone }) => ($tone === 'match' ? 'var(--success-500)' : 'var(--danger-500)')};
  color: ${({ $tone }) => ($tone === 'match' ? 'var(--success-700)' : 'var(--danger-500)')};
`;

const NoticeTitle = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
`;

const NoticeBody = styled.div`
  margin-top: 2px;
  font-size: var(--fs-sm);
  line-height: 1.4;
`;

export default EboxLookup;
